import { IconButton } from '@atlaskit/atlassian-navigation';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import BugIcon from '@atlaskit/icon/core/bug';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import StoryIcon from '@atlaskit/icon/core/story';
import ShowMoreHorizontal from '@atlaskit/icon/glyph/more';
import { LinkMenuItem } from '@atlaskit/navigation-system';
import { token } from '@atlaskit/tokens';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useIntl } from 'react-intl-next';
import { useRouter } from 'react-resource-router';

import { createFlowRoute } from '../../../../routes/utils';
import { useAnalyticsWebClient } from '../../../../utils/analytics/context';
import { useConfirmationModal } from '../../hooks/use-confirmation-modal';
import { useCompleteFlow } from '../hooks/use-complete-flow';

import messages from './messages';

type SessionType = 'story' | 'bug';

export const SessionItem = ({
	name,
	sessionType,
	sessionId,
	sessionARI,
}: {
	name: string;
	sessionType: SessionType;
	sessionId: string;
	sessionARI: string;
}) => {
	const { formatMessage } = useIntl();
	const [routeState, routerActions] = useRouter();
	const analyticsWebClient = useAnalyticsWebClient();
	const [completeSession, isCompletePending] = useCompleteFlow();
	const { openConfirmationModal, ConfirmationModal } = useConfirmationModal({
		title: formatMessage(messages.archiveSessionModalTitle),
		confirmBody: <p>{formatMessage(messages.archiveSessionWarningText)} </p>,
		onConfirm: () => {
			if (sessionId && sessionARI) {
				completeSession(sessionId, sessionARI);
			}
		},
	});
	const href = createFlowRoute(sessionId);

	const getSessionIcon = (type: SessionType) => {
		switch (type) {
			case 'bug':
				return <BugIcon color={token('color.icon.accent.red')} label="" />;
			case 'story':
			default:
				return <StoryIcon color={token('color.icon.accent.lime')} label="" />;
		}
	};

	const handleArchive = (e: MouseEvent | KeyboardEvent) => {
		e.stopPropagation();
		if (sessionId && sessionARI) {
			openConfirmationModal();
		}
	};

	return (
		<LinkMenuItem
			elemBefore={getSessionIcon(sessionType)}
			href={href}
			isSelected={routeState.match.url === `${routeState.basePath}${href}`}
			onClick={(e) => {
				if (href) {
					e.preventDefault();
					routerActions.push(href);

					analyticsWebClient.sendUIEvent({
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'boysenberry-session-item',
						source: 'boysenberry-sidebar',
					});
				}
			}}
			actionsOnHover={
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<IconButton
							ref={triggerRef}
							{...props}
							tooltip={formatMessage(messages.archiveSessionButtonTooltip)}
							icon={
								<ShowMoreHorizontal label={formatMessage(messages.archiveSessionButtonTooltip)} />
							}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem
							isDisabled={isCompletePending}
							onClick={handleArchive}
							elemBefore={<CheckMarkIcon label="" />}
						>
							{formatMessage(messages.archiveSessionButtonLabel)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			}
		>
			{name}
			{ConfirmationModal}
		</LinkMenuItem>
	);
};
